'use client';

import React from 'react';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useMonitoring } from '@kit/monitoring/hooks';
import { useSignInWithEmailPassword } from '@kit/supabase/hooks/use-sign-in-with-email-password';
import { useSignInWithPhone } from '@kit/supabase/hooks/use-sign-in-with-phone';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { useVerifyOtp } from '@kit/supabase/hooks/use-verify-otp';
import { useVerifyPhoneOtp } from '@kit/supabase/hooks/use-verify-phone-otp';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import { PhoneInput } from '@kit/ui/phone-input';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import { useCaptcha } from '../../captcha/client';
import { useLastAuthMethod } from '../../hooks/use-last-auth-method';
import { useLastAuthMethodPreference } from '../../hooks/use-last-auth-method-preference';
import { resolveAuthErrorMessage, isNetworkBlip } from '../../lib/auth-error-message';
import { PasswordSignInSchema } from '../../schemas/password-sign-in.schema';
import { PhoneSignInSchema } from '../../schemas/phone-auth.schema';
import { associateCustomerWithTenantAction } from '../../server/associate-customer-action';
import { identifyAccountAction } from '../../server/identify-account-action';
import { identifyAccountByPhoneAction } from '../../server/identify-phone-action';
import { prepareAndSendPhoneOtpAction } from '../../server/prepare-and-send-phone-otp';
import { resendEmailOtpAction } from '../../server/resend-email-otp-action';
import { NativeAuthButton } from './native-auth-button';
import { NativeAuthShell } from './native-auth-shell';
import {
  isBiometricAvailable,
  storeBiometricCredentials,
} from './native-biometric-service';
import { NativeGroupedInput, NativeInputRow } from './native-grouped-input';
import { NativeOtpScreen } from './native-otp-screen';
import { readSessionFromCookies } from './native-supabase-client';
import { markBiometricEnrolled } from './use-biometric-token-sync';

const ASSURANCE_LEVEL_2 = 'aal2';

// Mirrors the web methods: 'phone' = WhatsApp OTP, 'email' = email & password,
// 'otp' = email verification code.
type AuthMode = 'phone' | 'email' | 'otp';

const EmailOtpSchema = z.object({ email: z.string().email().max(320) });

type AuthProviders = {
  password: boolean;
  magicLink: boolean;
  otp: boolean;
  phone: boolean;
};

export function NativeSignInPage({
  logoUrl,
  accountName,
  primaryColor,
  secondaryColor,
  appBackgroundColor = '#FFFFFF',
  appTextColor = '#1C1C1C',
  signUpPath,
  passwordResetPath,
  providers,
  faceIdButton,
  tenantSlug,
}: {
  logoUrl?: string | null;
  accountName?: string;
  primaryColor: string;
  secondaryColor: string;
  appBackgroundColor?: string;
  appTextColor?: string;
  signUpPath: string;
  passwordResetPath: string;
  captchaSiteKey?: string;
  providers?: AuthProviders;
  faceIdButton?: React.ReactNode;
  tenantSlug?: string;
}) {
  const hasPhone = providers?.phone !== false;
  const hasEmail = providers?.password !== false;
  const hasOtp = providers?.otp !== false;

  // Same priority order as the web AUTH_METHOD_PRIORITY: email & password
  // first, then phone (WhatsApp), then email code. One form on screen at a
  // time; the alternatives render as text links below the active form.
  const availableModes = React.useMemo<AuthMode[]>(
    () => [
      ...(hasEmail ? (['email'] as const) : []),
      ...(hasPhone ? (['phone'] as const) : []),
      ...(hasOtp ? (['otp'] as const) : []),
    ],
    [hasEmail, hasPhone, hasOtp],
  );
  const { t } = useTranslation();
  const params = useSearchParams();
  const supabase = useSupabase();
  const monitoring = useMonitoring();
  const captcha = useCaptcha();
  const { recordAuthMethod } = useLastAuthMethod();

  const signInWithPhone = useSignInWithPhone();
  const signInWithEmail = useSignInWithEmailPassword();
  const verifyOtp = useVerifyPhoneOtp();
  const verifyEmailOtp = useVerifyOtp();

  // Server-action variant that pre-merges current tenant_slug into
  // auth.users.raw_user_meta_data before triggering the OTP. Used whenever a
  // tenantSlug is in scope, so the Send-SMS hook can resolve branding even
  // for returning users who signed up at a different tenant.
  const prepareAndSendOtp = useMutation({
    mutationFn: prepareAndSendPhoneOtpAction,
  });

  // Email prefill: sign-up claim banners (and the web claim flow) link here
  // with ?email= so the user doesn't retype the address they just used.
  const prefillEmail = params.get('email') ?? '';

  // Initial mode mirrors the web SignInMethodsContainer's resolution order:
  // an ?email arrival pins the first email-capable form; then the last-used
  // method (smart default — native records it too, via recordAuthMethod);
  // then priority order, email & password first.
  const preferredMethod = useLastAuthMethodPreference();

  const initialMode: AuthMode = (() => {
    if (prefillEmail && (hasEmail || hasOtp)) return hasEmail ? 'email' : 'otp';
    if (preferredMethod === 'password' && hasEmail) return 'email';
    if (preferredMethod === 'phone' && hasPhone) return 'phone';
    if (preferredMethod === 'otp' && hasOtp) return 'otp';
    return availableModes[0] ?? 'email';
  })();

  const [mode, setMode] = React.useState<AuthMode>(initialMode);
  const [otpSent, setOtpSent] = React.useState(false);
  const [otpPhone, setOtpPhone] = React.useState('');
  // Email-code mode: the address a code was just sent to (null = still on
  // the email form). Mirrors otpSent/otpPhone for the phone flow.
  const [otpEmailAddress, setOtpEmailAddress] = React.useState<string | null>(
    null,
  );
  const [resendCooldown, setResendCooldown] = React.useState(0);
  const [emailStep, setEmailStep] = React.useState<'identify' | 'password'>(
    'identify',
  );
  const [identifying, setIdentifying] = React.useState(false);
  const [isFinalizing, setIsFinalizing] = React.useState(false);

  // C1: an 'unknown' identify result renders an inline choice card instead of
  // auto-bouncing to sign-up. Customers who signed up on the OTHER channel
  // (or whose stored credential holds a typo) get a recovery path — trying
  // the other method — rather than being pushed into creating a duplicate.
  const [unknownAccount, setUnknownAccount] = React.useState<{
    channel: 'phone' | 'email';
    value: string;
  } | null>(null);
  const router = useRouter();

  const isDark = isDarkColor(appBackgroundColor);

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);


  /**
   * After a successful sign-in, read the real tokens from the SSR cookies
   * and store them in the Keychain for Face ID re-login.
   */
  const enrollBiometric = React.useCallback(async () => {
    try {
      const available = await isBiometricAvailable();
      if (!available) return;

      const session = readSessionFromCookies();
      if (!session?.refresh_token || !session?.user?.id) return;

      await storeBiometricCredentials(session.user.id, session.refresh_token);
      markBiometricEnrolled();
    } catch {
      // Non-critical — user is still signed in
    }
  }, []);

  const redirectAfterAuth = React.useCallback(
    async (inviteToken?: string | null) => {
      const next = params.get('next') ?? '/home';

      try {
        const assurance =
          await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

        if (assurance.error) {
          window.location.href = next;
          return;
        }

        const { nextLevel, currentLevel } = assurance.data;

        if (nextLevel === ASSURANCE_LEVEL_2 && nextLevel !== currentLevel) {
          const nextPath = inviteToken
            ? `/join?invite_token=${inviteToken}&next=${encodeURIComponent(next)}`
            : next;
          window.location.href = `/auth/verify?next=${encodeURIComponent(nextPath)}`;
          return;
        }

        if (inviteToken) {
          window.location.href = `/join?${new URLSearchParams({ invite_token: inviteToken, next }).toString()}`;
        } else {
          window.location.href = next;
        }
      } catch {
        window.location.href = next;
      }
    },
    [supabase, params],
  );

  // --- Phone mode ---
  const phoneForm = useForm<z.infer<typeof PhoneSignInSchema>>({
    resolver: zodResolver(PhoneSignInSchema),
    defaultValues: { phone: '' },
  });

  const handleSendOtp = async (data: z.infer<typeof PhoneSignInSchema>) => {
    // Identify-first: mirrors the email flow. Unknown phones and guest-
    // upgrade guests get routed to /auth/sign-up?phone=… (with name and
    // email_hint pre-filled for guest_upgrade) instead of just showing a
    // "no account found" toast that left the user stuck.
    setIdentifying(true);
    let identifyResult: Awaited<ReturnType<typeof identifyAccountByPhoneAction>>;
    try {
      identifyResult = await identifyAccountByPhoneAction({ phone: data.phone });
    } catch (error) {
      // Fall back to sending the OTP — the existing error path below covers
      // the rest.
      if (!isNetworkBlip(error)) {
        monitoring.captureException(
          error instanceof Error ? error : new Error('Identify phone failed'),
        );
      }
      identifyResult = { kind: 'exists' };
    } finally {
      setIdentifying(false);
    }

    if (identifyResult.kind === 'guest_upgrade') {
      // Working upgrade path — keep the auto-route.
      const qs = new URLSearchParams({ phone: data.phone });
      if (identifyResult.name) qs.set('name', identifyResult.name);
      if (identifyResult.emailHint)
        qs.set('email_hint', identifyResult.emailHint);
      router.push(`${signUpPath}?${qs.toString()}`);
      return;
    }

    if (identifyResult.kind === 'unknown') {
      // No hard bounce to sign-up: offer "try email instead" (they may have
      // signed up on the other channel) alongside "create account".
      setUnknownAccount({ channel: 'phone', value: data.phone });
      return;
    }

    setUnknownAccount(null);

    try {
      if (tenantSlug) {
        const result = await prepareAndSendOtp.mutateAsync({
          phone: data.phone,
          tenantSlug,
          captchaToken: captcha.token,
          shouldCreateUser: false,
        });

        if (!result.success) {
          throw new Error(result.error);
        }
      } else {
        await signInWithPhone.mutateAsync({
          phone: data.phone,
          captchaToken: captcha.token,
          shouldCreateUser: false,
        });
      }
      captcha.reset();
      setOtpPhone(data.phone);
      setOtpSent(true);
      setResendCooldown(30);
    } catch (error) {
      captcha.reset();
      const msg = error instanceof Error ? error.message : String(error ?? '');

      if (!isNetworkBlip(error)) {
        monitoring.captureException(
          error instanceof Error ? error : new Error(msg),
        );
      }
      // Surface real reasons (e.g. SMS rate-limit) instead of a flat "try again".
      toast.error(resolveAuthErrorMessage(error, t));
    }
  };

  const handleVerifyOtp = async (token: string) => {
    try {
      await verifyOtp.mutateAsync({
        phone: otpPhone,
        token,
        type: 'sms',
      });
      setIsFinalizing(true);
      recordAuthMethod('otp');

      associateCustomerWithTenantAction().catch((err) => {
        monitoring.captureException(
          err instanceof Error ? err : new Error('Tenant association failed'),
        );
      });
      await enrollBiometric();
      await redirectAfterAuth(params.get('invite_token'));
    } catch (error) {
      setIsFinalizing(false);
      if (!isNetworkBlip(error)) {
        monitoring.captureException(
          error instanceof Error ? error : new Error('OTP verify failed'),
        );
      }
      // Prefer a specific reason (e.g. "code expired"); fall back to the
      // OTP-specific "code didn't work" message rather than the generic one.
      toast.error(resolveAuthErrorMessage(error, t, 'auth:errors.otpInvalid'));
    }
  };

  const handleResendOtp = () => {
    setOtpSent(false);
    setResendCooldown(0);
  };

  // --- Email mode ---
  const emailForm = useForm<z.infer<typeof PasswordSignInSchema>>({
    resolver: zodResolver(PasswordSignInSchema),
    defaultValues: { email: prefillEmail, password: '' },
  });

  const handleIdentifyEmail = async () => {
    if (identifying) return;
    const email = emailForm.getValues('email').trim();
    const valid = await emailForm.trigger('email');
    if (!valid || !email) return;

    setIdentifying(true);
    try {
      const result = await identifyAccountAction({ email });

      if (result.kind === 'guest_upgrade') {
        // Working upgrade path — keep the auto-route.
        const qs = new URLSearchParams({ email });
        if (result.name) qs.set('name', result.name);
        if (result.phoneHint) qs.set('phone_hint', result.phoneHint);
        router.push(`${signUpPath}?${qs.toString()}`);
        return;
      }

      if (result.kind === 'unknown') {
        setUnknownAccount({ channel: 'email', value: email });
        return;
      }

      setUnknownAccount(null);
      setEmailStep('password');
    } catch (error) {
      // Fall back to showing the password field — worst case the user
      // hits the existing error path.
      if (!isNetworkBlip(error)) {
        monitoring.captureException(
          error instanceof Error ? error : new Error('Identify account failed'),
        );
      }
      setEmailStep('password');
    } finally {
      setIdentifying(false);
    }
  };

  const handleEmailSignIn = async (
    data: z.infer<typeof PasswordSignInSchema>,
  ) => {
    try {
      await signInWithEmail.mutateAsync(data);
      setIsFinalizing(true);
      recordAuthMethod('password', { email: data.email });

      associateCustomerWithTenantAction().catch((err) => {
        monitoring.captureException(
          err instanceof Error ? err : new Error('Tenant association failed'),
        );
      });
      await enrollBiometric();
      await redirectAfterAuth(params.get('invite_token'));
    } catch (error) {
      setIsFinalizing(false);
      if (!isNetworkBlip(error)) {
        monitoring.captureException(
          error instanceof Error ? error : new Error('Email sign-in failed'),
        );
      }
      // Surface the real Supabase reason (e.g. "Invalid login credentials",
      // "Email not confirmed") the same way the web AuthErrorAlert does, rather
      // than collapsing every failure into one generic toast. Falls back to the
      // generic message when the code/message has no mapped translation.
      toast.error(resolveAuthErrorMessage(error, t));
    }
  };

  // --- Email-code (otp) mode ---
  const otpForm = useForm<z.infer<typeof EmailOtpSchema>>({
    resolver: zodResolver(EmailOtpSchema),
    defaultValues: { email: prefillEmail },
  });

  const handleSendEmailOtp = async (data: z.infer<typeof EmailOtpSchema>) => {
    if (identifying) return;
    const email = data.email.trim();

    setIdentifying(true);
    try {
      // Identify-first, same as the other modes: guest upgrades route to
      // sign-up; unknown emails get the inline choice card.
      const result = await identifyAccountAction({ email });

      if (result.kind === 'guest_upgrade') {
        const qs = new URLSearchParams({ email });
        if (result.name) qs.set('name', result.name);
        if (result.phoneHint) qs.set('phone_hint', result.phoneHint);
        router.push(`${signUpPath}?${qs.toString()}`);
        return;
      }

      if (result.kind === 'unknown') {
        setUnknownAccount({ channel: 'email', value: email });
        return;
      }

      setUnknownAccount(null);

      // Existing usable account → send the branded 6-digit code (same
      // chokepoint the web email-code flow uses).
      const sent = await resendEmailOtpAction({ email });

      if (!sent.success) {
        throw new Error(sent.error);
      }

      setOtpEmailAddress(email);
      setResendCooldown(30);
    } catch (error) {
      if (!isNetworkBlip(error)) {
        monitoring.captureException(
          error instanceof Error ? error : new Error('Email OTP send failed'),
        );
      }
      toast.error(resolveAuthErrorMessage(error, t));
    } finally {
      setIdentifying(false);
    }
  };

  const handleVerifyEmailOtpToken = async (token: string) => {
    if (!otpEmailAddress) return;

    try {
      // type:'email' matches the token minted by signInWithOtp({ email }).
      await verifyEmailOtp.mutateAsync({
        type: 'email',
        email: otpEmailAddress,
        token,
      });
      setIsFinalizing(true);
      recordAuthMethod('otp', { email: otpEmailAddress });

      associateCustomerWithTenantAction().catch((err) => {
        monitoring.captureException(
          err instanceof Error ? err : new Error('Tenant association failed'),
        );
      });
      await enrollBiometric();
      await redirectAfterAuth(params.get('invite_token'));
    } catch (error) {
      setIsFinalizing(false);
      if (!isNetworkBlip(error)) {
        monitoring.captureException(
          error instanceof Error ? error : new Error('Email OTP verify failed'),
        );
      }
      toast.error(resolveAuthErrorMessage(error, t, 'auth:errors.otpInvalid'));
    }
  };

  // Shared by the switch links and the unknown-account card: entering a mode
  // always starts it from its first screen with any stale outcome cleared.
  const switchMode = (nextMode: AuthMode) => {
    setUnknownAccount(null);
    setEmailStep('identify');
    setMode(nextMode);
  };

  const isLoading =
    signInWithPhone.isPending ||
    prepareAndSendOtp.isPending ||
    signInWithEmail.isPending ||
    verifyOtp.isPending ||
    verifyEmailOtp.isPending;

  // Input style classes for dark/light bg
  const inputClass = isDark
    ? 'w-full bg-transparent text-base text-white outline-none placeholder:text-white/40'
    : 'w-full bg-transparent text-base text-gray-900 outline-none placeholder:text-gray-400';

  const finalizingOverlay = isFinalizing ? (
    <FullScreenAuthLoader
      appBackgroundColor={appBackgroundColor}
      appTextColor={appTextColor}
    />
  ) : null;

  // OTP verification screen
  if (otpSent) {
    return (
      <NativeAuthShell
        logoUrl={logoUrl}
        accountName={accountName}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        appBackgroundColor={appBackgroundColor}
        appTextColor={appTextColor}
        showBackButton
        onBack={handleResendOtp}
      >
        <NativeOtpScreen
          phone={otpPhone}
          loading={verifyOtp.isPending || isFinalizing}
          resendCooldown={resendCooldown}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          appBackgroundColor={appBackgroundColor}
          appTextColor={appTextColor}
          onVerify={handleVerifyOtp}
          onResend={handleResendOtp}
          onBack={handleResendOtp}
        />
        {finalizingOverlay}
      </NativeAuthShell>
    );
  }

  // Email-code verification screen (otp mode)
  if (otpEmailAddress) {
    const backToEmailForm = () => {
      setOtpEmailAddress(null);
      setResendCooldown(0);
    };

    return (
      <NativeAuthShell
        logoUrl={logoUrl}
        accountName={accountName}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        appBackgroundColor={appBackgroundColor}
        appTextColor={appTextColor}
        showBackButton
        onBack={backToEmailForm}
      >
        <NativeOtpScreen
          email={otpEmailAddress}
          loading={verifyEmailOtp.isPending || isFinalizing}
          resendCooldown={resendCooldown}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          appBackgroundColor={appBackgroundColor}
          appTextColor={appTextColor}
          onVerify={handleVerifyEmailOtpToken}
          onResend={backToEmailForm}
          onBack={backToEmailForm}
        />
        {finalizingOverlay}
      </NativeAuthShell>
    );
  }

  return (
    <NativeAuthShell
      logoUrl={logoUrl}
      primaryColor={primaryColor}
      secondaryColor={secondaryColor}
      appBackgroundColor={appBackgroundColor}
      appTextColor={appTextColor}
      showBackButton
    >
      <div className="flex flex-col gap-6">
        {/* Face ID button */}
        {faceIdButton && (
          <>
            {faceIdButton}
            <div className="flex items-center gap-3">
              <div className="h-px flex-1" style={{ backgroundColor: `${appTextColor}20` }} />
              <span className="text-xs" style={{ color: `${appTextColor}60` }}>
                <Trans i18nKey="auth:or" defaults="or" />
              </span>
              <div className="h-px flex-1" style={{ backgroundColor: `${appTextColor}20` }} />
            </div>
          </>
        )}

        {/* No-account-found card (C1) — replaces the old hard bounce to sign-up.
            The "try the other channel" target: an unknown phone offers the
            first email-capable mode; an unknown email offers phone. */}
        {unknownAccount && (() => {
          const tryOtherMode: AuthMode | null =
            unknownAccount.channel === 'phone'
              ? hasEmail
                ? 'email'
                : hasOtp
                  ? 'otp'
                  : null
              : hasPhone
                ? 'phone'
                : null;

          return (
          <div
            className="rounded-lg p-4 text-sm"
            style={{
              backgroundColor: `${appTextColor}10`,
              border: `1px solid ${appTextColor}40`,
              color: appTextColor,
            }}
            data-test="signin-unknown-account-card"
          >
            <p className="font-medium">
              {unknownAccount.channel === 'phone' ? (
                <Trans
                  i18nKey="auth:unknownPhoneHeading"
                  defaults="No account found for this phone number"
                />
              ) : (
                <Trans
                  i18nKey="auth:unknownEmailHeading"
                  defaults="No account found for this email"
                />
              )}
            </p>

            <p className="mt-1" style={{ color: `${appTextColor}99` }}>
              {tryOtherMode ? (
                <Trans
                  i18nKey="auth:unknownAccountBody"
                  defaults="You may have signed up with a different method — or you can create a new account."
                />
              ) : (
                <Trans
                  i18nKey="auth:unknownAccountBodySingle"
                  defaults="Check the details, or create a new account."
                />
              )}
            </p>

            <div className="mt-3 flex flex-col gap-2">
              {tryOtherMode && (
                <NativeAuthButton
                  type="button"
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  appTextColor={appTextColor}
                  onClick={() => switchMode(tryOtherMode)}
                  data-test="unknown-account-try-other"
                >
                  {unknownAccount.channel === 'phone' ? (
                    <Trans
                      i18nKey="auth:tryEmailInstead"
                      defaults="Try signing in with email"
                    />
                  ) : (
                    <Trans
                      i18nKey="auth:tryPhoneInstead"
                      defaults="Try signing in with phone"
                    />
                  )}
                </NativeAuthButton>
              )}

              <button
                type="button"
                onClick={() => {
                  const qs = new URLSearchParams(
                    unknownAccount.channel === 'phone'
                      ? { phone: unknownAccount.value }
                      : { email: unknownAccount.value },
                  );
                  router.push(`${signUpPath}?${qs.toString()}`);
                }}
                className="flex min-h-[44px] items-center justify-center rounded-xl px-4 text-sm font-medium"
                style={{
                  border: `1px solid ${appTextColor}40`,
                  color: appTextColor,
                }}
                data-test="unknown-account-create"
              >
                <Trans
                  i18nKey="auth:createNewAccount"
                  defaults="Create an account"
                />
              </button>

              <button
                type="button"
                onClick={() => setUnknownAccount(null)}
                className="flex min-h-[44px] items-center justify-center text-sm font-medium"
                style={{ color: `${appTextColor}99` }}
              >
                <Trans i18nKey="common:cancel" defaults="Cancel" />
              </button>
            </div>
          </div>
          );
        })()}

        {/* Phone form */}
        {hasPhone && mode === 'phone' && (
          <Form {...phoneForm}>
            <form
              onSubmit={phoneForm.handleSubmit(handleSendOtp)}
              className="flex flex-col gap-5"
            >
              <NativeGroupedInput appBackgroundColor={appBackgroundColor}>
                <FormField
                  control={phoneForm.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <NativeInputRow appTextColor={appTextColor}>
                          <PhoneInput
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={isLoading}
                            autoComplete="tel"
                            placeholder={t('auth:phoneNumberFormat')}
                            className={`native-phone-input border-0 bg-transparent text-base shadow-none focus-visible:ring-0 ${isDark ? 'text-white placeholder:text-white/40' : 'text-gray-900 placeholder:text-gray-400'}`}
                            data-test="native-phone-input"
                          />
                        </NativeInputRow>
                      </FormControl>
                      <FormMessage className="px-1 text-red-400" />
                    </FormItem>
                  )}
                />
              </NativeGroupedInput>

              {captcha.field}

              <NativeAuthButton
                type="submit"
                disabled={
                  isLoading || identifying || !phoneForm.formState.isValid
                }
                loading={
                  signInWithPhone.isPending ||
                  prepareAndSendOtp.isPending ||
                  identifying
                }
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                appTextColor={appTextColor}
              >
                <Trans i18nKey="auth:sendVerificationCode" />
              </NativeAuthButton>

              <ModeSwitchLinks
                current="phone"
                modes={availableModes}
                onSelect={switchMode}
                appTextColor={appTextColor}
              />
            </form>
          </Form>
        )}

        {/* Email form — two-step: identify by email, then enter password */}
        {hasEmail && mode === 'email' && (
          <Form {...emailForm}>
            <form
              onSubmit={
                emailStep === 'identify'
                  ? (e) => {
                      e.preventDefault();
                      void handleIdentifyEmail();
                    }
                  : emailForm.handleSubmit(handleEmailSignIn)
              }
              className="flex flex-col gap-5"
            >
              <NativeGroupedInput appBackgroundColor={appBackgroundColor}>
                <FormField
                  control={emailForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <NativeInputRow appTextColor={appTextColor}>
                          <input
                            type="email"
                            autoComplete="email"
                            placeholder="Email"
                            disabled={isLoading || identifying}
                            readOnly={emailStep === 'password'}
                            className={inputClass}
                            data-test="native-email-input"
                            {...field}
                          />
                        </NativeInputRow>
                      </FormControl>
                      <FormMessage className="px-1 text-red-400" />
                    </FormItem>
                  )}
                />
                {emailStep === 'password' && (
                  <FormField
                    control={emailForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <NativeInputRow appTextColor={appTextColor}>
                            <input
                              type="password"
                              autoComplete="current-password"
                              autoFocus
                              placeholder={t('auth:passwordPlaceholder', 'Password')}
                              disabled={isLoading}
                              className={inputClass}
                              data-test="native-password-input"
                              {...field}
                            />
                          </NativeInputRow>
                        </FormControl>
                        <FormMessage className="px-1 text-red-400" />
                      </FormItem>
                    )}
                  />
                )}
              </NativeGroupedInput>

              {emailStep === 'password' && (
                <>
                  {/* Use a different email */}
                  <div className="flex justify-between">
                    <button
                      type="button"
                      onClick={() => {
                        emailForm.setValue('password', '');
                        signInWithEmail.reset();
                        setEmailStep('identify');
                      }}
                      className="flex min-h-[44px] items-center text-sm font-medium"
                      style={{ color: appTextColor }}
                    >
                      <Trans i18nKey="auth:useDifferentEmail" />
                    </button>

                    <Link
                      href={passwordResetPath}
                      className="flex min-h-[44px] items-center text-sm font-medium"
                      style={{ color: appTextColor }}
                    >
                      <Trans i18nKey="auth:passwordForgottenQuestion" />
                    </Link>
                  </div>
                </>
              )}

              {captcha.field}

              <NativeAuthButton
                type="submit"
                disabled={isLoading || identifying || isFinalizing}
                loading={
                  emailStep === 'identify'
                    ? identifying
                    : signInWithEmail.isPending || isFinalizing
                }
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                appTextColor={appTextColor}
              >
                {emailStep === 'identify' ? (
                  <Trans i18nKey="common:continue" />
                ) : (
                  <Trans i18nKey="auth:signIn" />
                )}
              </NativeAuthButton>

              {emailStep === 'identify' && (
                <ModeSwitchLinks
                  current="email"
                  modes={availableModes}
                  onSelect={switchMode}
                  appTextColor={appTextColor}
                />
              )}
            </form>
          </Form>
        )}

        {/* Email-code form (otp mode) — email only, then the code screen */}
        {hasOtp && mode === 'otp' && (
          <Form {...otpForm}>
            <form
              onSubmit={otpForm.handleSubmit(handleSendEmailOtp)}
              className="flex flex-col gap-5"
            >
              <NativeGroupedInput appBackgroundColor={appBackgroundColor}>
                <FormField
                  control={otpForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <NativeInputRow appTextColor={appTextColor}>
                          <input
                            type="email"
                            autoComplete="email"
                            placeholder="Email"
                            disabled={isLoading || identifying}
                            className={inputClass}
                            data-test="native-otp-email-input"
                            {...field}
                          />
                        </NativeInputRow>
                      </FormControl>
                      <FormMessage className="px-1 text-red-400" />
                    </FormItem>
                  )}
                />
              </NativeGroupedInput>

              <p className="px-1 text-xs" style={{ color: `${appTextColor}80` }}>
                <Trans
                  i18nKey="auth:methodHeaderOtpDescription"
                  defaults="No password needed — we'll email you a one-time code."
                />
              </p>

              {captcha.field}

              <NativeAuthButton
                type="submit"
                disabled={isLoading || identifying || isFinalizing}
                loading={identifying}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                appTextColor={appTextColor}
              >
                <Trans i18nKey="auth:sendVerificationCode" />
              </NativeAuthButton>

              <ModeSwitchLinks
                current="otp"
                modes={availableModes}
                onSelect={switchMode}
                appTextColor={appTextColor}
              />
            </form>
          </Form>
        )}

        {/* Legal links */}
        <div className="flex justify-center gap-4 pt-2">
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs"
            style={{ color: `${appTextColor}60` }}
          >
            <Trans i18nKey="auth:privacyPolicy" defaults="Privacy Policy" />
          </a>
          <span className="text-xs" style={{ color: `${appTextColor}30` }}>|</span>
          <a
            href="/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs"
            style={{ color: `${appTextColor}60` }}
          >
            <Trans i18nKey="auth:termsOfService" defaults="Terms of Service" />
          </a>
        </div>
      </div>

      {/* Global native phone input overrides */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .native-phone-input button[type="button"] {
              background: transparent !important;
              border: none !important;
              box-shadow: none !important;
              color: ${appTextColor} !important;
            }
            .native-phone-input input {
              background: transparent !important;
              border: none !important;
              box-shadow: none !important;
              color: ${appTextColor} !important;
            }
            .native-phone-input input::placeholder {
              color: ${appTextColor}66 !important;
            }
          `,
        }}
      />
      {finalizingOverlay}
    </NativeAuthShell>
  );
}

// One text link per non-active method, in the shared priority order — the
// native counterpart of the web methods container's alternatives footer, so
// switching between all enabled methods works the same on both surfaces.
function ModeSwitchLinks({
  current,
  modes,
  onSelect,
  appTextColor,
}: {
  current: AuthMode;
  modes: AuthMode[];
  onSelect: (mode: AuthMode) => void;
  appTextColor: string;
}) {
  const others = modes.filter((m) => m !== current);

  if (others.length === 0) return null;

  return (
    <div className="flex flex-col">
      {others.map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onSelect(m)}
          className="flex min-h-[44px] items-center justify-center text-sm font-medium"
          style={{ color: appTextColor }}
          data-test={`native-auth-method-${m}`}
        >
          {m === 'phone' ? (
            <Trans
              i18nKey="auth:continueWithWhatsApp"
              defaults="Continue with WhatsApp"
            />
          ) : m === 'email' ? (
            <Trans
              i18nKey="auth:continueWithPassword"
              defaults="Continue with email & password"
            />
          ) : (
            <Trans
              i18nKey="auth:continueWithEmailOtp"
              defaults="Continue with email code"
            />
          )}
        </button>
      ))}
    </div>
  );
}

function FullScreenAuthLoader({
  appBackgroundColor,
  appTextColor,
}: {
  appBackgroundColor: string;
  appTextColor: string;
}) {
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: appBackgroundColor }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        stroke={appTextColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="animate-spin"
      >
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>
  );
}

function isDarkColor(hex: string): boolean {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
}
